import React, { useState, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HomeIcon from '@mui/icons-material/Home';
import { exportDataToFile, importDataFromFile } from '../utils/importExport';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';

const Header = ({ onSearch, onLanguageChange }) => {
  const { conversations, themes, tags, refreshData, clearAllData } = useData();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  // メニュー状態
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const settingsOpen = Boolean(settingsAnchorEl);
  
  // ダイアログ状態
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // スナックバー状態
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });


  // データ管理メニューを開く
  const handleDataMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // データ管理メニューを閉じる
  const handleDataMenuClose = () => {
    setAnchorEl(null);
  };

  // インポートダイアログを開く
  const handleImportClick = () => {
    handleDataMenuClose();
    setImportDialogOpen(true);
  };

  // エクスポートダイアログを開く
  const handleExportClick = () => {
    handleDataMenuClose();
    setExportDialogOpen(true);
  };

  // インポートダイアログを閉じる
  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
  };

  // エクスポートダイアログを閉じる
  const handleExportDialogClose = () => {
    setExportDialogOpen(false);
  };

  // ファイル選択ダイアログを開く
  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  // ファイルが選択されたときの処理
  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // 処理中のスナックバーを表示
    setSnackbar({
      open: true,
      message: `${files.length}個のファイルをインポート中...`,
      severity: 'info'
    });
    
    let successCount = 0;
    let errorCount = 0;
    
    // 各ファイルを処理
    for (let i = 0; i < files.length; i++) {
      try {
        console.log('インポート開始:', files[i].name);
        const result = await importDataFromFile(files[i], { overwriteExisting: false });
        console.log('インポート結果:', result);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`ファイルインポートエラー (${files[i].name}):`, error);
        errorCount++;
      }
    }
    
    // 結果のスナックバーを表示
    let message = `インポート完了: 成功 ${successCount}個, 失敗 ${errorCount}個`;
    if (successCount > 0) {
      message += ' (既存データとの重複は保護されました)';
    }
    
    setSnackbar({
      open: true,
      message: message,
      severity: errorCount === 0 ? 'success' : (successCount > 0 ? 'warning' : 'error')
    });
    
    // ダイアログを閉じる
    setImportDialogOpen(false);
    
    // ファイル入力をリセット
    event.target.value = null;
    
    // インポートが1つでも成功した場合はデータを更新
    if (successCount > 0) {
      // データを更新
      refreshData();
    }
  };

  // データをエクスポート
  const handleExportData = () => {
    const result = exportDataToFile();
    
    // エクスポート結果のスナックバーを表示
    setSnackbar({
      open: true,
      message: result ? 'データのエクスポートに成功しました' : 'データのエクスポートに失敗しました',
      severity: result ? 'success' : 'error'
    });
    
    // ダイアログを閉じる
    setExportDialogOpen(false);
  };

  // スナックバーを閉じる
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 設定メニューを開く
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  // 設定メニューを閉じる
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // データ削除ダイアログを開く
  const handleDeleteDataClick = () => {
    handleSettingsClose();
    setDeleteDialogOpen(true);
  };

  // データ削除ダイアログを閉じる
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  // データを全削除
  const handleDeleteAllData = () => {
    const result = clearAllData();
    
    setDeleteDialogOpen(false);
    
    setSnackbar({
      open: true,
      message: result ? 'すべてのデータが削除されました' : 'データの削除に失敗しました',
      severity: result ? 'success' : 'error'
    });
  };

  // ランディングページに戻る
  const handleGoToLanding = () => {
    navigate('/');
  };

  // 英語版に切り替え
  const handleSwitchToEnglish = () => {
    if (onLanguageChange) {
      onLanguageChange('en');
    }
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          SynaptiQore
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={handleGoToLanding}
            sx={{ mr: 2 }}
          >
            ホーム
          </Button>
          
          <Button
            color="inherit"
            startIcon={<SettingsIcon />}
            onClick={handleSettingsClick}
            sx={{ ml: 2 }}
          >
            設定
          </Button>
          
          <Button
            color="inherit"
            startIcon={<CloudUploadIcon />}
            onClick={handleDataMenuClick}
            sx={{ ml: 2 }}
          >
            データ管理
          </Button>

          {/* 言語切り替えボタン */}
          <Button
            color="inherit"
            onClick={handleSwitchToEnglish}
            sx={{ 
              ml: 2,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            English
          </Button>
        </Box>
      </Toolbar>

      {/* 設定メニュー */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={settingsOpen}
        onClose={handleSettingsClose}
      >
        <MenuItem onClick={handleDeleteDataClick}>
          <DeleteForeverIcon fontSize="small" sx={{ mr: 1 }} />
          データを全削除
        </MenuItem>
      </Menu>

      {/* データ管理メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleDataMenuClose}
      >
        <MenuItem onClick={handleImportClick}>
          <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} />
          インポート
        </MenuItem>
        <MenuItem onClick={handleExportClick}>
          <CloudDownloadIcon fontSize="small" sx={{ mr: 1 }} />
          エクスポート
        </MenuItem>
      </Menu>

      {/* インポートダイアログ */}
      <Dialog open={importDialogOpen} onClose={handleImportDialogClose}>
        <DialogTitle>データのインポート</DialogTitle>
        <DialogContent>
          <DialogContentText>
            以前にエクスポートしたJSONファイルからデータをインポートします。
            複数のファイルを選択して一度にインポートすることができます。
            新しいデータは既存のデータとマージされます。
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleFileSelect}
              startIcon={<CloudUploadIcon />}
            >
              ファイルを選択 (複数可)
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".json"
              onChange={handleFileChange}
              multiple
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportDialogClose}>キャンセル</Button>
        </DialogActions>
      </Dialog>

      {/* エクスポートダイアログ */}
      <Dialog open={exportDialogOpen} onClose={handleExportDialogClose}>
        <DialogTitle>データのエクスポート</DialogTitle>
        <DialogContent>
          <DialogContentText>
            現在のデータをJSONファイルとしてエクスポートします。
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              エクスポートされるデータ:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • 会話: {conversations.length}件
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • テーマ: {themes.length}件
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • タグ: {tags.length}件
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportDialogClose}>キャンセル</Button>
          <Button onClick={handleExportData} variant="contained">
            エクスポート
          </Button>
        </DialogActions>
      </Dialog>

      {/* データ削除ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>データの全削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            すべてのデータを削除します。この操作は元に戻せません。
            本当に全データを削除しますか？
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              削除されるデータ:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • 会話: {conversations.length}件
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • テーマ: {themes.length}件
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • タグ: {tags.length}件
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
              警告: この操作は取り消せません！
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>キャンセル</Button>
          <Button onClick={handleDeleteAllData} variant="contained" color="error">
            全データを削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppBar>
  );
};

export default Header;
